import React, { useEffect, useRef, useState } from 'react';
import {
  PlayFill,
  Plus,
  Trash3,
  ArrowRight,
  HourglassSplit,
  CheckCircleFill,
  XCircleFill,
} from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import { fetchTasks, submitBriefVideos } from '../api/creators';
import './CreatorTabs.scss';

// "Feladatok" tab — only briefs the creator was selected for, now and in the past:
//   • CONFIRMED → upload + submit, counting down to the DELIVERY deadline,
//   • IN REVIEW → videos submitted, waiting for feedback,
//   • history: APPROVED, or not approved once the brief is completed.
// Open, applied and not-selected briefs live on the Briefs tab.
//
// Data comes from GET /creators/tasks (fetchTasks).

// ── Countdown hook ──────────────────────────────────────────────────────────
// Returns { days, hours, minutes, seconds, over, total } for a target date,
// ticking every second. `dateStr` is an ISO date (YYYY-MM-DD → end of that day).
function useCountdown(dateStr) {
  const target = React.useMemo(() => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return null;
    // Treat the deadline as the END of the given day (23:59:59).
    d.setHours(23, 59, 59, 999);
    return d.getTime();
  }, [dateStr]);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (target == null) return null;
  const diff = target - now;
  const over = diff <= 0;
  const s = Math.max(0, Math.floor(diff / 1000));
  return {
    over,
    total: diff,
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
}

// Urgency tier from remaining time — drives the countdown colour.
function urgency(cd) {
  if (!cd || cd.over) return 'over';
  const hoursLeft = cd.total / 3600000;
  if (hoursLeft <= 48) return 'critical'; // ≤ 2 days
  if (hoursLeft <= 24 * 5) return 'soon'; // ≤ 5 days
  return 'normal';
}

function Countdown({ dateStr, labelKey }) {
  const { t } = useLocale();
  const cd = useCountdown(dateStr);
  if (!cd) return null;
  const tier = urgency(cd);

  return (
    <div className={`countdown countdown--${tier}`}>
      <span className="countdown__label">{t(labelKey)}</span>
      {cd.over ? (
        <span className="countdown__over">{t('tasks.deadlinePassed')}</span>
      ) : (
        <span className="countdown__clock">
          <Unit n={cd.days} label={t('tasks.d')} />
          <Unit n={cd.hours} label={t('tasks.h')} pad />
          <Unit n={cd.minutes} label={t('tasks.m')} pad />
          <Unit n={cd.seconds} label={t('tasks.s')} pad />
        </span>
      )}
    </div>
  );
}

function Unit({ n, label, pad }) {
  const val = pad ? String(n).padStart(2, '0') : String(n);
  return (
    <span className="countdown__unit">
      <span className="countdown__num">{val}</span>
      <span className="countdown__u">{label}</span>
    </span>
  );
}

export default function TasksTab({ onBrowseBriefs }) {
  const { t } = useLocale();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = React.useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      setError(false);
    }
    try {
      setTasks(await fetchTasks());
    } catch {
      if (!silent) setError(true);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const reloadSilently = React.useCallback(() => load({ silent: true }), [load]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="panel-card">
        <div className="panel-card__muted">{t('shell.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="panel-card">
        <div className="panel-card__muted">{t('briefs.loadError')}</div>
        <button className="btn-o btn-o--dark btn-o--sm" onClick={() => load()}>
          {t('shell.retry')}
        </button>
      </div>
    );
  }

  const withOutcome = tasks.map((b) => ({ ...b, outcome: taskOutcome(b) }));
  const byOutcome = (outcome) => withOutcome.filter((b) => b.outcome === outcome);
  const active = byOutcome('confirmed');
  const inReview = byOutcome('in_review');
  const past = withOutcome.filter((b) => b.outcome === 'approved' || b.outcome === 'not_approved');

  const nothing = active.length + inReview.length + past.length === 0;

  return (
    <div className="tasks">
      {nothing && (
        <div className="panel-card">
          <div className="panel-card__muted">{t('tasks.empty')}</div>
          <button className="task-card__cta" onClick={onBrowseBriefs}>
            {t('tasks.browseBriefs')}
            <ArrowRight size={15} />
          </button>
        </div>
      )}

      {active.length > 0 && (
        <section className="tasks__section">
          <h2 className="tasks__heading">
            {t('tasks.activeHeading')}
            <span className="tasks__count">{active.length}</span>
          </h2>
          {active.map((brief) => (
            <ActiveTask key={brief.id} brief={brief} onSubmitted={reloadSilently} />
          ))}
        </section>
      )}

      {inReview.length > 0 && (
        <section className="tasks__section">
          <h2 className="tasks__heading">
            {t('tasks.reviewHeading')}
            <span className="tasks__count tasks__count--review">{inReview.length}</span>
          </h2>
          {inReview.map((brief) => (
            <StatusTask key={brief.id} brief={brief} />
          ))}
        </section>
      )}

      {past.length > 0 && (
        <section className="tasks__section">
          <h2 className="tasks__heading tasks__heading--muted">
            {t('tasks.pastHeading')}
            <span className="tasks__count tasks__count--muted">{past.length}</span>
          </h2>
          {past.map((brief) => (
            <StatusTask key={brief.id} brief={brief} />
          ))}
        </section>
      )}
    </div>
  );
}

// ── Confirmed brief: upload + submit, delivery countdown ────────────────────
function ActiveTask({ brief, onSubmitted }) {
  const { t } = useLocale();
  const [slots, setSlots] = useState(() => Array(brief.requiredVideos).fill(null));
  const [notice, setNotice] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    return () => slots.forEach((s) => s?.url && URL.revokeObjectURL(s.url));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setSlot(index, value) {
    setSlots((prev) => {
      const next = [...prev];
      if (prev[index]?.url) URL.revokeObjectURL(prev[index].url);
      next[index] = value;
      return next;
    });
    setNotice('');
  }
  function onPick(index, file) {
    if (!file || !file.type.startsWith('video/')) return;
    setSlot(index, { file, url: URL.createObjectURL(file) });
  }

  const done = slots.filter(Boolean).length;
  const total = brief.requiredVideos;
  const [submitted, setSubmitted] = useState(false);
  const [progress, setProgress] = useState(0);
  const locked = submitting || submitted;

  async function handleSubmit() {
    if (done !== total) {
      setNotice(t('briefs.submitAllFirst', { total }));
      return;
    }
    setNotice('');
    setProgress(0);
    setSubmitting(true);
    try {
      const files = slots.map((s) => s.file);
      const slotIndexes = slots.map((_, i) => i);
      await submitBriefVideos(brief.id, files, slotIndexes, setProgress);
      setSubmitted(true);
      if (onSubmitted) onSubmitted();
      setNotice(t('briefs.submitted'));
    } catch {
      setNotice(t('briefs.submitError'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="task-card task-card--active">
      <div className="task-card__top">
        <div>
          {brief.brand && (
            <div className="task-card__brand">
              {brief.brand.logo ? (
                <img src={brief.brand.logo} alt="" />
              ) : (
                <span className="task-card__brand-ph">
                  {brief.brand.name?.[0]?.toUpperCase() || 'B'}
                </span>
              )}
              {brief.brand.name}
            </div>
          )}
          <h3 className="task-card__title">{brief.title}</h3>
        </div>
        <Countdown dateStr={brief.deadline} labelKey="tasks.deliveryIn" />
      </div>

      <div className="task-card__uploaded">
        {t('briefs.uploaded', { done, total })}
      </div>
      <div className="slot-grid">
        {slots.map((slot, i) => (
          <TaskSlot
            key={i}
            slot={slot}
            locked={locked}
            uploading={submitting}
            onPick={(file) => onPick(i, file)}
            onRemove={() => setSlot(i, null)}
          />
        ))}
      </div>

      <div className="task-card__foot">
        <button className="btn-o btn-o--dark btn-o--sm" onClick={handleSubmit} disabled={locked}>
          {submitting && <span className="btn-spinner" aria-hidden="true" />}
          {submitting
            ? progress < 100
              ? t('briefs.uploading', { percent: progress })
              : t('briefs.submitting')
            : submitted
              ? t('briefs.submittedShort')
              : t('briefs.submit')}
        </button>
        {notice && <span className="task-card__notice">{notice}</span>}
      </div>
    </div>
  );
}

function taskOutcome(brief) {
  if (brief.creatorState === 'approved') return 'approved';
  const worked = brief.creatorState === 'confirmed' || brief.creatorState === 'in_review';
  if (!worked) return brief.creatorState;
  if (brief.campaignStatus === 'completed') return 'not_approved';
  const results = Array.isArray(brief.results) ? brief.results : [];
  const reviewed = results.length > 0 && results.every((r) => r.status !== 'submitted');
  if (reviewed && results.some((r) => r.status === 'rejected')) return 'not_approved';
  return brief.creatorState;
}

const STATUS_CARDS = {
  in_review: { tone: 'review', banner: 'applied', Icon: HourglassSplit, title: 'tasks.reviewTitle', body: 'tasks.reviewBody' },
  approved: { tone: 'approved', banner: 'done', Icon: CheckCircleFill, title: 'tasks.approvedTitle', body: 'tasks.approvedBody' },
  not_approved: { tone: 'rejected', banner: 'rejected', Icon: XCircleFill, title: 'tasks.notApprovedTitle', body: 'tasks.notApprovedBody' },
};

function StatusTask({ brief }) {
  const { t } = useLocale();
  const card = STATUS_CARDS[brief.outcome];
  if (!card) return null;
  const { Icon } = card;
  return (
    <div className={`task-card task-card--${card.tone}`}>
      <div className="task-card__top">
        <div>
          {brief.brand && (
            <div className="task-card__brand">
              {brief.brand.logo ? (
                <img src={brief.brand.logo} alt="" />
              ) : (
                <span className="task-card__brand-ph">
                  {brief.brand.name?.[0]?.toUpperCase() || 'B'}
                </span>
              )}
              {brief.brand.name}
            </div>
          )}
          <h3 className="task-card__title">{brief.title}</h3>
        </div>
        <span className={`brief-card__status is-${brief.outcome}`}>
          {t(`briefs.state.${brief.outcome}`)}
        </span>
      </div>

      <div className={`brief-status brief-status--${card.banner}`}>
        <Icon size={18} />
        <div>
          <div className="brief-status__title">{t(card.title)}</div>
          <div className="brief-status__body">{t(card.body)}</div>
        </div>
      </div>

      <TaskResults results={brief.results} />
    </div>
  );
}

const RESULT_STATUS_KEYS = {
  submitted: 'tasks.result.submitted',
  approved: 'tasks.result.approved',
  rejected: 'tasks.result.rejected',
};

const EARNING_STATUS_KEYS = {
  pending: 'tasks.earning.pending',
  processing: 'tasks.earning.processing',
  completed: 'tasks.earning.completed',
  failed: 'tasks.earning.failed',
  cancelled: 'tasks.earning.cancelled',
};

function formatMoney(amount, currency) {
  if (amount === null || amount === undefined) return null;
  return `${Number(amount).toLocaleString()} ${(currency || '').toUpperCase()}`.trim();
}

function TaskResults({ results }) {
  const { t } = useLocale();
  if (!Array.isArray(results) || results.length === 0) return null;

  const approvedCount = results.filter((r) => r.status === 'approved').length;
  const earnings = results.map((r) => r.earning).filter((e) => e && e.amount != null && e.status !== 'cancelled');
  const currency = earnings[0]?.currency || '';
  const earned = earnings.reduce((sum, e) => sum + Number(e.amount), 0);

  return (
    <div className="task-results">
      <div className="task-results__head">
        <span className="task-results__title">{t('tasks.resultsTitle')}</span>
        <span className="task-results__summary">
          {t('tasks.resultsSummary', { approved: approvedCount, total: results.length })}
          {earnings.length > 0 && ` · ${formatMoney(earned, currency)}`}
        </span>
      </div>
      <div className="task-results__list">
        {results.map((r, i) => (
          <div key={r.id} className={`task-result task-result--${r.status}`}>
            {r.url ? (
              <video className="task-result__video" src={r.url} controls playsInline preload="metadata" />
            ) : (
              <div className="task-result__video task-result__video--empty"><PlayFill size={22} /></div>
            )}
            <div className="task-result__body">
              <div className="task-result__top">
                <span className="task-result__name">
                  {t('tasks.videoN', { n: (r.slotIndex ?? i) + 1 })}
                </span>
                <span className={`task-result__status is-${r.status}`}>
                  {RESULT_STATUS_KEYS[r.status] ? t(RESULT_STATUS_KEYS[r.status]) : r.status}
                </span>
              </div>
              {r.reviewNote && (
                <p className="task-result__note">
                  <span>{t('tasks.reviewNote')}:</span> {r.reviewNote}
                </p>
              )}
              {r.earning && (
                <div className="task-result__earning">
                  {formatMoney(r.earning.amount, r.earning.currency)}
                  {EARNING_STATUS_KEYS[r.earning.status] && ` · ${t(EARNING_STATUS_KEYS[r.earning.status])}`}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TaskSlot({ slot, locked, uploading, onPick, onRemove }) {
  const { t } = useLocale();
  const inputRef = useRef(null);

  if (slot) {
    return (
      <div className="slot slot--filled">
        <video className="slot__video" src={slot.url} muted playsInline preload="metadata" />
        {uploading ? (
          <span className="slot__uploading" aria-hidden="true">
            <span className="btn-spinner btn-spinner--lg" />
          </span>
        ) : (
          <span className="slot__play" aria-hidden="true">
            <PlayFill size={22} />
          </span>
        )}
        {!locked && (
          <button
            type="button"
            className="slot__remove"
            onClick={onRemove}
            title={t('briefs.remove')}
            aria-label={t('briefs.remove')}
          >
            <Trash3 size={15} />
          </button>
        )}
        <span className="slot__name" title={slot.file.name}>
          {slot.file.name}
        </span>
      </div>
    );
  }

  return (
    <button type="button" className="slot slot--empty" onClick={() => inputRef.current?.click()}>
      <span className="slot__plus">
        <Plus size={26} />
      </span>
      <span className="slot__hint">{t('briefs.slotHint')}</span>
      <span className="slot__any">{t('briefs.slotAny')}</span>
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        hidden
        onChange={(e) => {
          onPick(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </button>
  );
}
